import React, { type FC } from 'react';
import { Building2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';
import { trackCatalogRedirect } from '../../../lib/jobs-api';

export const ApplicationPreviewCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('seeker');
  const jobTitle = safeGet<string>(result, 'job', 'title');
  const companyName = safeGet<string>(result, 'job', 'company', 'name');
  const catalogJobId = safeGet<string>(result, 'job', 'id');
  const cvTitle = safeGet<string>(result, 'cv', 'title');
  const coverLetter = safeGet<string>(result, 'coverLetter');
  const readyToSubmit = safeGet<boolean>(result, 'readyToSubmit');
  const redirectRequired = safeGet<boolean>(result, 'redirectRequired');
  const redirectUrl = safeGet<string>(result, 'redirectUrl');
  const existingApplication = safeGet<unknown>(result, 'existingApplication');
  const hasCv = safeGet<unknown>(result, 'cv') != null;
  const canOpenExternalApply = Boolean(redirectRequired && redirectUrl && catalogJobId);

  const openExternalApply = () => {
    if (!redirectUrl) return;
    const opened =
      typeof window !== 'undefined'
        ? window.open(redirectUrl, '_blank', 'noopener,noreferrer')
        : null;
    if (catalogJobId) {
      void trackCatalogRedirect(catalogJobId, { redirectUrl }).catch(() => {});
    }
    if (!opened && typeof window !== 'undefined') {
      window.location.assign(redirectUrl);
    }
  };

  // Determine status
  let statusDotColor: string;
  let statusLabel: string;
  if (canOpenExternalApply) {
    statusDotColor = 'var(--text-secondary)';
    statusLabel = t('jobs.externalApply');
  } else if (readyToSubmit) {
    statusDotColor = 'var(--text-secondary)';
    statusLabel = 'Ready to apply';
  } else if (existingApplication != null) {
    statusDotColor = 'var(--text-secondary)';
    statusLabel = 'Already applied';
  } else if (!hasCv) {
    statusDotColor = 'var(--text-secondary)';
    statusLabel = 'Missing CV';
  } else {
    statusDotColor = 'var(--text-secondary)';
    statusLabel = 'Ready to apply';
  }

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Job title */}
        {jobTitle && (
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {jobTitle}
          </div>
        )}

        {/* Company name */}
        {companyName && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Building2 size={13} style={{ flexShrink: 0 }} />
            <span>{companyName}</span>
          </div>
        )}

        {/* Status indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 13,
            color: 'var(--text-secondary)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: statusDotColor,
              flexShrink: 0,
            }}
          />
          <span>{statusLabel}</span>
        </div>

        {/* CV line */}
        {hasCv && cvTitle && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
            }}
          >
            Using: {cvTitle}
          </div>
        )}

        {/* Cover letter preview */}
        {coverLetter && (
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-primary)',
              lineHeight: '20px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {coverLetter}
          </div>
        )}

        {canOpenExternalApply && (
          <button
            type="button"
            onClick={openExternalApply}
            style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 999,
              padding: '8px 12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
              background: 'var(--surface-elevated)',
              cursor: 'pointer',
            }}
          >
            <ExternalLink size={14} />
            {t('jobs.applyOnWebsite')}
          </button>
        )}
      </div>
    </CardShell>
  );
};
